// src/com/veh_info/VehInfoRented.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import SearchBox from '../../common/SearchBox';
import {
    HoriContentContainer, ContentConainer, ContentHeader, ContentBody
} from '../../common/TopViewControl';
import { AppObject, AppStyle } from '../../value/AppConst';
import {
    dcInfoListWithRentVehCountRepo, dcInfoSelectedWithRentVehCountRepo, vehInfoListRentRepo
} from '../../value/AppModel';
import { LIST_VEH_INFO_OF_DC } from './VehInfoGql';
import { LIST_DC_INFO_WITH_RENT_VEH_COUNT } from '../dc_info/DcInfoGql';
import { VehInfoDictionary } from './VehInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { DcInfoDictionary } from '../dc_info/DcInfoDict';
import ValueMisc from '../../value/ValueMisc';
import { CircularProgress } from '@mui/material';

const ColumnBox = styled(Box)({display:'flex', alignItems:'center', height:'100%'});
const ControlBox = styled(Box)({display:'flex', alignItems:'center'});
const NameBox = styled(Box)({flexGrow:1, display:'flex', alignItems:'center'});
const GuideLabel = styled(Box)({
    borderRadius:20, border: AppStyle.BorderCCC, padding:'5px 15px', });
const RoundLabel = styled(Box)({
    borderRadius:20, backgroundColor:AppStyle.PrimaryRGB, color:'white', padding:'5px 15px', fontWeight:'bold', marginRight:5
});

const DcTableProps = DcInfoDictionary.dcListWithRentVehCountProps;

/**
 * (VEH_INFO) 정보 View Controller
 * DC별 용차 목록.
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-07 12:55:53 using DbdbTap
 */
export default function VehInfoRented({
    viewServiceTags, // for switching view
    show = true,
}) {
    const dcInfoListWithRentVehCountData = useReactiveVar(dcInfoListWithRentVehCountRepo);
    const vehInfoListData = useReactiveVar(vehInfoListRentRepo);
    const dcSelected = useReactiveVar(dcInfoSelectedWithRentVehCountRepo);
    const [searchDcText, setSearchDcText] = useState('');
    const [searchVehText, setSearchVehText] = useState('');
    const addAlert = useAlertSetter();

    // ##### Call GraphQL to get List #####
    const [getDcInfoListWithRentVehCount, responseDcInfoList] = useLazyQuery(LIST_DC_INFO_WITH_RENT_VEH_COUNT, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetDcInfoList(data, option)},
        onError: (error) => addAlert({title: 'DC 목록 조회 오류', message: [error.message]})
    });

    const [getVehInfoList, responseList] = useLazyQuery(LIST_VEH_INFO_OF_DC, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: VehInfoDictionary.errorTitle.Get, message: [error.message]})
    });

    useEffect(()=>{
        const diff = new Date().getTime()/1000 - dcInfoListWithRentVehCountData.when;
        if(diff > 300)
            getDcInfoListWithRentVehCount();
    }, []);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetDcInfoList = (data, clientOption) => {
        if(data.dcInfoListWithRentVehCount) {
            dcInfoListWithRentVehCountRepo({when:new Date().getTime()/1000, records:data.dcInfoListWithRentVehCount});
        }
    };

    const onCompleteGetList = (data, clientOption) => {
        if(data.vehInfoListOfDc) {
            vehInfoListRentRepo({when:new Date().getTime()/1000, records:data.vehInfoListOfDc});
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onChangeSearchDcText = (text) => {
        setSearchDcText(text);
    };

    const onChangeSearchVehText = (text) => {
        setSearchVehText(text);
    };

    const onClickDcInfo = (dcInfo) => {
        dcInfoSelectedWithRentVehCountRepo(dcInfo);
        getVehInfoList({variables:{dcCd:dcInfo.dcCd, rentYn:'Y'}});
    }

    const loadingAny = responseDcInfoList.loading || responseList.loading;

    const renderVehListTitle = () => {
        if(dcSelected.dcName) {
            return(
                <NameBox>
                    <RoundLabel>{dcSelected.dcName}</RoundLabel>
                    <RoundLabel>{dcSelected.dcTypeName}</RoundLabel>
                    <RoundLabel>{ValueMisc.int2str(dcSelected.countVehicle)}대</RoundLabel>
                </NameBox>
            );
        }
        else {
            return(
                <NameBox><GuideLabel>센터를 선택하세요</GuideLabel></NameBox>
            );
        }
    };

    const rootStyle = show ? undefined : {display:'none', visibility:'hidden', pointerEvents:'none'};

    return(
        <ContentConainer style={rootStyle}>
            {viewServiceTags}
            <ContentBody>
                <HoriContentContainer>
                    <ColumnBox width={500}>
                        <ContentConainer>
                            <ContentHeader>
                                <ControlBox>
                                    <SearchBox
                                        value={searchDcText}
                                        onChangeText={onChangeSearchDcText}
                                    />
                                </ControlBox>
                            </ContentHeader>
                            <FlexyTable
                                name={DcTableProps.tableId}
                                tableProps={DcTableProps}
                                records={dcInfoListWithRentVehCountData.records}
                                search={searchDcText}
                                onRowClicked={onClickDcInfo}
                            />
                        </ContentConainer>
                    </ColumnBox>
                    <ColumnBox style={{flexGrow:1}}>
                        <ContentConainer>
                            <ContentHeader>
                                {renderVehListTitle()}
                                <ControlBox>
                                    {
                                        loadingAny &&
                                        <CircularProgress size={20} />
                                    }
                                    <SearchBox
                                        value={searchVehText}
                                        onChangeText={onChangeSearchVehText}
                                    />
                                </ControlBox>
                            </ContentHeader>
                            <FlexyTable
                                name={VehInfoDictionary.tableProps.tableId}
                                tableProps={VehInfoDictionary.tableProps}
                                records={vehInfoListData.records}
                                search={searchVehText}
                            />
                        </ContentConainer>
                    </ColumnBox>
                </HoriContentContainer>
            </ContentBody>
        </ContentConainer>
    );
}
